/**
 * One-shot, per-build client storage migrations.
 *
 * WHY THIS EXISTS
 * ---------------
 * The server pre-launch wipe (D1 + R2, server migration 0029) reset every
 * account to a clean slate, but it could only touch SERVER state. The
 * Capacitor WebView on a tester's device still holds the old localStorage /
 * IndexedDB it wrote before the wipe, and an iOS hard-reset does NOT clear
 * WebView storage. So a device that ran an earlier build keeps surfacing
 * stale, now-orphaned data:
 *   - old "detail upload" sprite overrides (e.g. a tester's Dragonite /
 *     Zamazenta crop) read back by the Dex grid even though the server no
 *     longer has the matching record;
 *   - stale scan-history / scan-override snapshots that no longer line up
 *     with the wiped account.
 * This module clears exactly those caches ONCE per device when a new build
 * first launches, then stamps a flag so it never runs again.
 *
 * PATTERN FOR FUTURE BUILD MIGRATIONS (numbered flags)
 * ----------------------------------------------------
 * Each build that needs a one-shot client cleanup adds its own numbered
 * guard flag, e.g. `build_migration_applied_234`. The runner checks the flag,
 * does its work, then sets the flag to "1". Flags are independent and
 * monotonic: shipping build 240 with a NEW `build_migration_applied_240`
 * block will run for everyone regardless of whether 234 ran, because the new
 * flag has never been set on any device. Never reuse an old flag for new
 * work; add a new numbered block instead.
 *
 * THE ALLOWLIST APPROACH (and why a blanket clear is wrong)
 * ---------------------------------------------------------
 * We clear an explicit ALLOWLIST of stale-data keys, never
 * `localStorage.clear()`. A blanket clear would also destroy data the user
 * must keep across the migration:
 *   - the auth/session bearer token (a blanket clear logs the user out);
 *   - theme + scan settings + visibility/linkage preferences;
 *   - permission-prime / push-prompt / onboarding "asked once" flags
 *     (clearing these re-prompts for notifications, which iOS then treats as
 *     permanently denied);
 *   - the dex state itself, which has its OWN dedicated wipe migration
 *     (see dexWipeMigration.ts) and must not be double-handled here.
 * So we enumerate only the keys that hold orphaned sprite/scan caches and
 * remove those, leaving everything else untouched.
 *
 * NOT EVERY MIGRATION IS A WIPE. A numbered block may also REWRITE a stored
 * value in place — see the 1.8.5 nickname-preset refresh below, which asks a
 * module that owns its own storage key to do the rewrite rather than reaching
 * into localStorage from here.
 */
import { refreshBuiltInNicknamePresets } from "./nicknameTemplateStore";

/** Guard flag for the build-234 one-shot wipe. */
const BUILD_234_FLAG = "build_migration_applied_234";

/**
 * Guard flag for the 1.8.5 nickname-preset refresh. A device sitting on an
 * UNMODIFIED built-in preset picked it from a menu that was both narrower and
 * partly broken (the league marker ignored its style; there was no light
 * maxed-stat highlight; everything was superscript), so the pick is refreshed
 * to that preset's current definition exactly once. Owner direction: "their
 * choice was limited by what was available at the time, and it wasn't working
 * correctly." See refreshBuiltInNicknamePresets for whose template it touches
 * and, more importantly, whose it does not.
 */
const NICKNAME_PRESET_REFRESH_FLAG = "build_migration_applied_185_presets";

/**
 * Exact localStorage keys to remove for the build-234 wipe.
 *
 * - gx.sprite.v1.__index / gx.dexcrop.v1.__index: the index blobs for the two
 *   on-device crop stores (localSpriteStore / dexCropCapture). The actual
 *   crop entries live under the matching prefixes below and are removed by
 *   the prefix sweep.
 * - gx.scanRecordOverrides.v1: manual per-record scan overrides.
 * - gx.scanHistory.v2: cached scan-history snapshots.
 */
const BUILD_234_EXACT_KEYS: readonly string[] = [
  "gx.sprite.v1.__index",
  "gx.dexcrop.v1.__index",
  "gx.scanRecordOverrides.v1",
  "gx.scanHistory.v2",
];

/**
 * Key PREFIXES whose every entry is removed for the build-234 wipe. The two
 * crop stores key each entry as `<prefix><fingerprint>`, so we sweep all keys
 * starting with these prefixes (this also covers the `__index` blobs above,
 * which are listed explicitly for clarity).
 */
const BUILD_234_KEY_PREFIXES: readonly string[] = [
  "gx.sprite.v1.",
  "gx.dexcrop.v1.",
];

/**
 * IndexedDB databases to delete for the build-234 wipe.
 *
 * Intentionally EMPTY. The app's only IndexedDB database is
 * `nexus_dex_prefs`, which stores the notification-permission "asked once"
 * decision (see notifications.ts). Deleting it would re-prompt for
 * notifications, which iOS treats as a permanent denial, so it is preserved
 * per the allowlist rule. No app IndexedDB holds stale sprite/trade data.
 */
const BUILD_234_INDEXED_DBS: readonly string[] = [];

function removeKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage blocked or quota error - skip this key, never block boot */
  }
}

/** Remove every localStorage key that starts with one of the given prefixes. */
function sweepPrefixes(prefixes: readonly string[]): void {
  let keys: string[] = [];
  try {
    keys = Object.keys(window.localStorage);
  } catch {
    return;
  }
  for (const key of keys) {
    if (prefixes.some((p) => key.startsWith(p))) {
      removeKey(key);
    }
  }
}

/** Best-effort delete of an IndexedDB database. Never throws, never blocks. */
function deleteIndexedDb(name: string): void {
  try {
    if (typeof indexedDB === "undefined") return;
    indexedDB.deleteDatabase(name);
  } catch {
    /* not a runtime with IndexedDB, or delete blocked - skip */
  }
}

/**
 * Run all pending one-shot client storage migrations. Safe to call on every
 * launch: each numbered block self-guards on its flag and is a no-op once
 * applied. Wrapped in try/catch so a storage failure can never block boot.
 *
 * Call this synchronously at the very top of the app entry point, before any
 * store/context provider initialises, so stale data is gone before hydration.
 */
export function runBuildMigrations(): void {
  try {
    if (typeof window === "undefined") return;

    // ── build 234: one-shot stale sprite/scan cache wipe ──────────────────
    if (window.localStorage.getItem(BUILD_234_FLAG) !== "1") {
      for (const key of BUILD_234_EXACT_KEYS) removeKey(key);
      sweepPrefixes(BUILD_234_KEY_PREFIXES);
      for (const dbName of BUILD_234_INDEXED_DBS) deleteIndexedDb(dbName);

      try {
        window.localStorage.setItem(BUILD_234_FLAG, "1");
      } catch {
        /* quota - worst case the wipe re-runs next launch, which is harmless */
      }
      // eslint-disable-next-line no-console
      console.log("[buildMigration] 234 storage wipe applied");
    }

    // ── 1.8.5: refresh unmodified built-in nickname presets ───────────────
    if (window.localStorage.getItem(NICKNAME_PRESET_REFRESH_FLAG) !== "1") {
      const changed = refreshBuiltInNicknamePresets();
      try {
        window.localStorage.setItem(NICKNAME_PRESET_REFRESH_FLAG, "1");
      } catch {
        /* quota: worst case it re-runs next launch, which is idempotent */
      }
      if (changed) {
        // eslint-disable-next-line no-console
        console.log("[buildMigration] 1.8.5 nickname preset refresh applied");
      }
    }
  } catch {
    /* a migration failure must never block app launch */
  }
}
